import React, { useState } from 'react';
import Icon from '../../../components/AppIcon';
import Image from '../../../components/AppImage';
import Button from '../../../components/ui/Button';
import { Checkbox } from '../../../components/ui/Checkbox';
import VisaStatusBadge from './VisaStatusBadge';
import UrgencyIndicator from './UrgencyIndicator';
import { useLanguage } from '../../../contexts/LanguageContext';

const VisaApplicationTable = ({ applications, onSelectApplication, onBulkSelect, selectedIds }) => {
  const { t, isRTL } = useLanguage();
  const [sortConfig, setSortConfig] = useState({ key: 'applicationDate', direction: 'desc' });

  const handleSort = (key) => {
    setSortConfig({
      key,
      direction: sortConfig?.key === key && sortConfig?.direction === 'asc' ? 'desc' : 'asc'
    });
  };

  const sortedApplications = [...applications]?.sort((a, b) => {
    if (sortConfig?.key === 'applicationDate') {
      return sortConfig?.direction === 'asc' 
        ? new Date(a.applicationDate) - new Date(b.applicationDate)
        : new Date(b.applicationDate) - new Date(a.applicationDate);
    }
    if (sortConfig?.key === 'expirationDate') {
      return sortConfig?.direction === 'asc'
        ? new Date(a.expirationDate) - new Date(b.expirationDate)
        : new Date(b.expirationDate) - new Date(a.expirationDate);
    }
    return 0;
  });

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    // Always use en-US locale for universal date format (Western numerals)
    return date?.toLocaleDateString('en-US', { day: '2-digit', month: '2-digit', year: 'numeric' });
  };

  const calculateDaysRemaining = (expirationDate) => {
    const today = new Date();
    const expiry = new Date(expirationDate);
    const diffTime = expiry - today;
    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
    return diffDays;
  };

  const allSelected = applications?.length > 0 && selectedIds?.length === applications?.length;
  const someSelected = selectedIds?.length > 0 && selectedIds?.length < applications?.length;

  return (
    <div className="bg-card rounded-lg border border-border shadow-warm overflow-hidden">
      <div className="overflow-x-auto">
        <table className="w-full">
          <thead className="bg-muted/50 border-b border-border">
            <tr>
              <th className="px-4 py-3 text-left">
                <Checkbox
                  checked={allSelected}
                  indeterminate={someSelected}
                  onChange={(e) => {
                    if (e?.target?.checked) {
                      onBulkSelect(applications?.map(app => app?.id));
                    } else {
                      onBulkSelect([]);
                    }
                  }}
                />
              </th>
              <th className="px-4 py-3 text-left text-sm font-semibold text-foreground">
                {t('visas.pilgrimNameLabel')}
              </th>
              <th 
                className="px-4 py-3 text-left text-sm font-semibold text-foreground cursor-pointer hover:text-primary transition-smooth"
                onClick={() => handleSort('applicationDate')}
              >
                <div className="flex items-center gap-2">
                  {t('visas.applicationDate')}
                  <Icon 
                    name={sortConfig?.key === 'applicationDate' && sortConfig?.direction === 'asc' ? 'ChevronUp' : 'ChevronDown'} 
                    size={16} 
                  />
                </div>
              </th>
              <th className="px-4 py-3 text-left text-sm font-semibold text-foreground">
                {t('visas.status')}
              </th>
              <th className="px-4 py-3 text-left text-sm font-semibold text-foreground">
                {t('visas.stage')}
              </th>
              <th 
                className="px-4 py-3 text-left text-sm font-semibold text-foreground cursor-pointer hover:text-primary transition-smooth"
                onClick={() => handleSort('expirationDate')}
              >
                <div className="flex items-center gap-2">
                  {t('visas.expiration')}
                  <Icon 
                    name={sortConfig?.key === 'expirationDate' && sortConfig?.direction === 'asc' ? 'ChevronUp' : 'ChevronDown'} 
                    size={16} 
                  />
                </div>
              </th>
              <th className="px-4 py-3 text-left text-sm font-semibold text-foreground">
                {t('visas.urgency')}
              </th>
              <th className="px-4 py-3 text-right text-sm font-semibold text-foreground">
                {t('visas.actions')}
              </th>
            </tr>
          </thead>
          <tbody className="divide-y divide-border">
            {sortedApplications?.map((application) => {
              const daysRemaining = calculateDaysRemaining(application?.expirationDate);
              const isSelected = selectedIds?.includes(application?.id);

              return (
                <tr 
                  key={application?.id}
                  className={`hover:bg-muted/30 transition-smooth ${isSelected ? 'bg-primary/5' : ''}`}
                >
                  <td className="px-4 py-4">
                    <Checkbox
                      checked={isSelected}
                      onChange={(e) => {
                        if (e?.target?.checked) {
                          onBulkSelect([...selectedIds, application?.id]);
                        } else {
                          onBulkSelect(selectedIds?.filter(id => id !== application?.id));
                        }
                      }}
                    />
                  </td>
                  <td className="px-4 py-4">
                    <div className="flex items-center gap-3">
                      <Image
                        src={application?.pilgrimPhoto}
                        alt={application?.pilgrimPhotoAlt}
                        className="w-10 h-10 rounded-full object-cover"
                      />
                      <div>
                        <p className="text-sm font-medium text-foreground">{application?.pilgrimName}</p>
                        <p className="caption text-muted-foreground">{application?.passportNumber}</p>
                      </div>
                    </div>
                  </td>
                  <td className="px-4 py-4">
                    <p className="text-sm text-foreground">{formatDate(application?.applicationDate)}</p>
                  </td>
                  <td className="px-4 py-4">
                    <VisaStatusBadge status={application?.status} size="sm" />
                  </td>
                  <td className="px-4 py-4">
                    <p className="text-sm text-foreground">{application?.processingStage}</p>
                  </td>
                  <td className="px-4 py-4">
                    <div>
                      <p className="text-sm text-foreground">{formatDate(application?.expirationDate)}</p>
                      {daysRemaining <= 30 && daysRemaining > 0 && (
                        <p className="caption text-warning mt-1">{daysRemaining} {t('visas.daysRemaining')}</p>
                      )}
                      {daysRemaining <= 0 && (
                        <p className="caption text-destructive mt-1">{t('visas.expired')}</p>
                      )}
                    </div>
                  </td>
                  <td className="px-4 py-4">
                    <UrgencyIndicator level={application?.urgency} daysRemaining={daysRemaining > 0 ? daysRemaining : 0} />
                  </td>
                  <td className="px-4 py-4 text-right">
                    <Button
                      variant="ghost"
                      size="sm"
                      iconName="Eye"
                      onClick={() => onSelectApplication(application)}
                    >
                      {t('visas.view')}
                    </Button>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
      {applications?.length === 0 && (
        <div className="p-12 text-center">
          <Icon name="FileText" size={48} className="text-muted-foreground mx-auto mb-4" />
          <p className="text-muted-foreground">{t('visas.noApplicationsFound')}</p>
        </div>
      )}
    </div>
  );
};

export default VisaApplicationTable;